import { FC, Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { ReactComponent as ExitIcon } from "@images/exit_icon.svg";
import { ReactComponent as LoadingSpinner } from "@images/loading_spinner.svg";
import { NLMail } from "@models/Models";
import { getIndividualMail } from "@utils/managers/MailManager";
import { useOnboardingProfile } from "@providers/OnboardingContext";
import IconButton from "@components/common/IconButton";
import { previewDiscoverRecommendation } from "@utils/managers/networking/NetworkManager";

interface PreviewMessageModalProps {
	open: boolean;
	onClose: () => void;
	messageId?: string;
	isDiscover?: boolean;
}

const PreviewMessageModal: FC<PreviewMessageModalProps> = ({ open, onClose, messageId, isDiscover }) => {
	const { onboardingProfile } = useOnboardingProfile();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [mail, setMail] = useState<NLMail | undefined>(undefined);

	useEffect(() => {
		if (!open) {
			setMail(undefined);
			setError(null);
			setIsLoading(false);
			return;
		}

		if (!messageId || !onboardingProfile?.persistence.mailboxProfile) {
			setError("Preview unavailable");
			return;
		}

		setIsLoading(true);
		setError(null);

		if (isDiscover) {
			previewDiscoverRecommendation(messageId).then((mail) => {
				setMail(mail);
				setIsLoading(false);
			}).catch((error) => {
				setError("Failed to load preview");
				setIsLoading(false);
			});
		} else {
			getIndividualMail([onboardingProfile.persistence.mailboxProfile], messageId, (fetchedMail, fetchError) => {
				if (fetchError || !fetchedMail) {
					setError("Failed to load preview");
					setIsLoading(false);
					return;
				}
				setMail(fetchedMail);
				setIsLoading(false);
			});
		}


	}, [open, messageId, onboardingProfile]);

	const formattedHtml = mail?.body_html ? `<html><head><base target=\"_blank\"></head><body>${mail.body_html}</body></html>` : undefined;
	const titleText = mail?.sender_name;

	return (
		<Transition as={Fragment} show={open}>
			<Dialog onClose={onClose} className="relative z-40">
				<Transition.Child as="div" enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
					<div className="fixed inset-0 bg-black bg-opacity-70" aria-hidden="true" />
				</Transition.Child>
				<div className="fixed inset-0 w-screen h-screen p-6 sm:p-8 md:p-10">
					<Transition.Child enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95" className={"h-full"}>
						<Dialog.Panel className="w-full h-full transform overflow-hidden rounded-2xl bg-white align-middle shadow-2xl transition-all relative flex flex-col">
							<div className="flex items-center justify-between px-4 py-3 border-b border-black/10 bg-white">
                            <div className="text-black font-medium font-primary text-xl">Preview: {titleText}</div>
                                <IconButton Icon={ExitIcon} className="-mr-2.5" iconClassName="!fill-black" onClick={() => onClose()} />
							</div>
							<div className="flex-1 overflow-auto">
								{isLoading ? (
									<div className="w-full h-full flex items-center justify-center">
										<LoadingSpinner className="h-[28px] text-black/20 animate-spin fill-black" />
									</div>
								) : error ? (
									<div className="w-full h-full flex items-center justify-center">
										<div className="text-black/60 font-primary">{error}</div>
									</div>
								) : (
									<iframe title={mail?.subject} className="bg-white w-full h-full" srcDoc={formattedHtml}></iframe>
								)}
							</div>
						</Dialog.Panel>
					</Transition.Child>
				</div>
			</Dialog>
		</Transition>
	);
};

export default PreviewMessageModal;


